/**
 * HoldingFinancePanel - Panel CONFIDENTIEL CA Holding (apporteur d'affaires).
 *
 * Visible UNIQUEMENT pour Jean LE BOUEDEC (CEO) + Charles Canone (Nord).
 * Le frontend verifie via GET /api/ceo/me/holding-access avant de monter.
 *
 * Affiche :
 *  - CA Holding brut (commissions encaissees par la Holding du partenaire travaux)
 *  - Retrocession 5% reversee au partenaire travaux
 *  - CA Holding NET (brut - retro)
 *  - Par projet et par partenaire travaux
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null) return '—';
  return Math.round(n).toLocaleString('fr-FR') + ' EUR';
};

const HoldingFinancePanel = ({ branch = null }) => {
  const [allowed, setAllowed] = useState(null);  // null = checking, true/false = decided
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('projects');

  // Verifier l'acces avant tout
  useEffect(() => {
    axios.get(`${API}/ceo/me/holding-access`).then(r => setAllowed(!!r.data?.has_holding_access)).catch(() => setAllowed(false));
  }, []);

  const fetchData = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const params = branch && branch !== 'consolide' ? { branch } : {};
      const r = await axios.get(`${API}/ceo/holding-finance`, { params });
      setData(r.data);
    } catch (e) {
      // silencieux : si 403 on n'affiche rien
    } finally { setLoading(false); }
  }, [allowed, branch]);

  useEffect(() => { if (allowed) fetchData(); }, [allowed, fetchData]);

  if (allowed !== true) return null;  // pas autorise -> n'affiche rien

  const summary = data?.summary || {};
  const projects = data?.projects || [];
  const partners = data?.partners || [];

  return (
    <div className="realease-card p-0 overflow-hidden" data-testid="holding-finance-panel">
      {/* Header confidentiel */}
      <div className="bg-gradient-to-r from-[#1a1d2e] to-[#2a2f4e] px-5 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-white text-lg flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            CA Holding (Confidentiel)
          </h3>
          <p className="text-xs text-gray-300 mt-0.5">Commissions apporteur d'affaires encaissees par la Holding. La Holding reverse 5% a RealEase. Visible Jean + Charles uniquement.</p>
        </div>
        <span className="text-[10px] text-amber-400 border border-amber-400/40 px-2 py-1 rounded uppercase tracking-wider">Confidentiel</span>
      </div>

      {loading && <div className="p-5 text-sm text-gray-400 italic">Chargement...</div>}

      {!loading && (
        <>
          {/* Stats globales */}
          <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-gray-100 bg-gray-50/50">
            <div className="text-center" data-testid="holding-summary-brut">
              <p className="text-[10px] uppercase text-gray-500">CA Holding brut HT</p>
              <p className="text-xl font-bold text-gray-900">{fmt(summary.total_brut_ht)}</p>
              <p className="text-[10px] text-gray-400">TTC : {fmt(summary.total_brut_ttc)}</p>
            </div>
            <div className="text-center" data-testid="holding-summary-retro">
              <p className="text-[10px] uppercase text-amber-700">Retro {summary.retro_pct || 5}% vers RealEase</p>
              <p className="text-xl font-bold text-amber-700">- {fmt(summary.total_retro_ht)}</p>
              <p className="text-[10px] text-gray-400">Reversee a RealEase</p>
            </div>
            <div className="text-center bg-emerald-50 rounded-lg py-2" data-testid="holding-summary-net">
              <p className="text-[10px] uppercase text-emerald-800 font-bold">CA Holding NET</p>
              <p className="text-2xl font-bold text-emerald-700">{fmt(summary.total_net_ht)}</p>
              <p className="text-[10px] text-emerald-600">TTC : {fmt(summary.total_net_ttc)}</p>
            </div>
            <div className="text-center" data-testid="holding-summary-pending">
              <p className="text-[10px] uppercase text-gray-500">Encaisse / Pending</p>
              <p className="text-sm font-bold text-gray-700">
                <span className="text-emerald-700">{fmt(summary.total_received_ht)}</span> /
                <span className="text-amber-700"> {fmt(summary.total_pending_ht)}</span>
              </p>
              <p className="text-[10px] text-gray-400">Recu / A recevoir</p>
            </div>
          </div>

          {/* Onglets Projets / Partenaires */}
          <div className="flex border-b border-gray-200" data-testid="holding-tabs">
            <button onClick={() => setActiveTab('projects')}
                    className={`px-4 py-2 text-xs font-semibold ${activeTab === 'projects' ? 'border-b-2 border-[#13736D] text-[#13736D]' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid="holding-tab-projects">
              Par projet ({projects.length})
            </button>
            <button onClick={() => setActiveTab('partners')}
                    className={`px-4 py-2 text-xs font-semibold ${activeTab === 'partners' ? 'border-b-2 border-[#13736D] text-[#13736D]' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid="holding-tab-partners">
              Par partenaire travaux ({partners.length})
            </button>
          </div>

          <div className="p-5">
            {activeTab === 'projects' ? (
              projects.length === 0 ? (
                <p className="text-xs text-gray-400 italic text-center py-4">
                  Aucun flux Holding pour le moment. Ajoutez une commission "encaissee par la Holding" depuis l'onglet finances du projet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="holding-projects-table">
                    <thead className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-2 pr-2">Projet</th>
                        <th className="text-left py-2 pr-2">Client</th>
                        <th className="text-right py-2 pr-2">Brut HT</th>
                        <th className="text-right py-2 pr-2">Retro 5% (→ RealEase)</th>
                        <th className="text-right py-2 pr-2">NET HT (Holding)</th>
                        <th className="text-right py-2 pr-2">Encaisse</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {projects.map(p => (
                        <tr key={p.project_id} data-testid={`holding-project-${p.project_id}`}>
                          <td className="py-2 pr-2">
                            <a href={`/projects/${p.project_id}`} className="font-medium text-[#13736D] hover:underline">{p.name}</a>
                            <span className="ml-1 text-[10px] uppercase text-gray-400">{p.branch}</span>
                          </td>
                          <td className="py-2 pr-2 text-gray-600">{p.client_name || '—'}</td>
                          <td className="py-2 pr-2 text-right">{fmt(p.ca_holding_brut_ht)}</td>
                          <td className="py-2 pr-2 text-right text-amber-700">- {fmt(p.retro_partner_ht)}</td>
                          <td className="py-2 pr-2 text-right font-bold text-emerald-700">{fmt(p.ca_holding_net_ht)}</td>
                          <td className="py-2 pr-2 text-right text-xs">
                            <span className="text-emerald-700">{fmt(p.received_ht)}</span>
                            {p.pending_ht > 0 && <span className="text-amber-700"> + {fmt(p.pending_ht)} pending</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              partners.length === 0 ? (
                <p className="text-xs text-gray-400 italic text-center py-4">Aucun partenaire travaux pour le moment.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="holding-partners-table">
                    <thead className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-2 pr-2">Partenaire travaux</th>
                        <th className="text-right py-2 pr-2">Nb projets</th>
                        <th className="text-right py-2 pr-2">CA brut HT</th>
                        <th className="text-right py-2 pr-2">Retro 5%</th>
                        <th className="text-right py-2 pr-2">NET pour Holding</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {partners.map(p => (
                        <tr key={p.partner} data-testid={`holding-partner-${p.partner}`}>
                          <td className="py-2 pr-2 font-medium text-gray-800">{p.partner}</td>
                          <td className="py-2 pr-2 text-right text-gray-600">{p.projects_count}</td>
                          <td className="py-2 pr-2 text-right">{fmt(p.ca_holding_brut_ht)}</td>
                          <td className="py-2 pr-2 text-right text-amber-700">- {fmt(p.retro_ht)}</td>
                          <td className="py-2 pr-2 text-right font-bold text-emerald-700">{fmt(p.ca_holding_net_ht)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default HoldingFinancePanel;
